/**
 * AddEnrollmentModal.jsx — Iter 168.b (Danny)
 *
 * One-click "Add product" quick action on the Customer Focus page.
 *
 * Use case: an existing Live customer wants to sign up for an additional
 * product OR a rep upsells a customer to a new product line. Either way
 * we need a fast way to:
 *   1. Pick a product from the catalog
 *   2. Choose status (Pending → puts the customer into Implementation
 *      and stamps the appropriate track stage strip on the Customer
 *      Focus header / Active → no implementation needed, stays Live)
 *   3. Optionally set monthly value + patient count
 *
 * Behind the scenes:
 *   POST /api/contacts/{id}/enrollments  (creates the enrollment record
 *     AND mirrors into enrolled_products so the bucket derivation picks
 *     it up — Iter 168.b backend fix in contact_enrollments_routes.py).
 *
 * Caller is expected to refresh customer state (loadAll) after the
 * onCreated callback fires so the new stage strip renders immediately.
 */
import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Plus, X, Loader2, ChevronDown, ChevronRight } from "lucide-react";

const STATUS_OPTIONS = [
  { id: "pending",  label: "Pending / In implementation", desc: "Adds the customer to the Implementation bucket and starts the track timeline." },
  { id: "active",   label: "Active / Live",                desc: "Customer is already live on this product. No implementation needed." },
];

// Iter 171 — canonical category order matches the EHI Product Catalog
// spec (Feb 2026) so the picker renders products in the same order
// they appear in the Quote Catalog page and Quote Builder.
const CATEGORY_ORDER = [
  "Application / Software",
  "Ancillary Products",
  "Service",
  "Care Coordination Service",
  "Care Coordination Application",
];

export default function AddEnrollmentModal({ open, onClose, contactId, catalog, currentProducts = [], apiBase, token, onCreated }) {
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("pending");
  const [patientCount, setPatientCount] = useState("");
  const [monthlyValue, setMonthlyValue] = useState("");
  const [priceMode, setPriceMode] = useState("preset"); // preset | custom — Iter 171.2
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Iter 171.2 — selected product's catalog entry (used to derive
  // pre-selectable prices/tiers, just like the Quote Builder).
  const selectedDef = product ? (catalog?.[product] || null) : null;
  const isPerPatient = selectedDef?.model === "per_patient" && (selectedDef?.list_price || selectedDef?.per_patient);
  const isPercentage = selectedDef?.model === "percentage";
  // Quick-pick monthly $ tiers for fixed-fee products. Tiers escalate
  // through the most common deal sizes Danny + Josh quote daily.
  const FIXED_TIERS = [250, 500, 1000, 2500, 5000, 10000];
  // Per-patient panel counts: most common patient pools quoted.
  const PATIENT_TIERS = [50, 100, 200, 500, 1000];
  // Percentage tiers (% of monthly collections).
  const PERCENT_TIERS = [2, 4, 6, 8];

  // Picking a product auto-fills the price from the catalog list rate.
  // Iter 171.G (Danny §5): tier_group enforces radio behavior — picking
  // one tier in a group (e.g. EHR Essential) silently replaces any
  // currently-staged sibling (EHR Pro). Since the modal only ever
  // stages one product at a time this is implicit; we keep the field
  // so future multi-stage flows can rely on it.
  const handleProductSelect = (slug) => {
    setProduct(slug);
    setPriceMode("preset");
    const def = catalog?.[slug];
    if (!def) return;
    if (def.model === "per_patient" && (def.list_price || def.per_patient)) {
      const rate = Number(def.list_price ?? def.per_patient ?? 0);
      const count = Number(patientCount) || 0;
      setMonthlyValue(String(Math.round(rate * count)));
    } else if (def.unit === "custom" || def.list_price === 0) {
      // Contact-for-pricing products: blank the price field so the rep
      // is prompted to type the negotiated rate themselves.
      setMonthlyValue("");
      setPriceMode("custom");
    } else {
      setMonthlyValue(String(def.list_price ?? 0));
    }
  };

  // Per-patient products: recompute monthly $ when the rep changes
  // patient count (only while still in `preset` price mode — once they
  // click `Custom` we leave the $ alone).
  const handlePatientCountChange = (val) => {
    setPatientCount(val);
    if (priceMode !== "preset") return;
    if (!selectedDef || selectedDef.model !== "per_patient") return;
    const rate = Number(selectedDef.list_price ?? selectedDef.per_patient ?? 0);
    const count = Number(val) || 0;
    setMonthlyValue(String(Math.round(rate * count)));
  };

  // Iter 171 — group available products by their spec category, hide
  // legacy slugs flagged `hidden: true`, and render each group with a
  // section header. Customer can't re-enroll into a product they
  // already have, so those are filtered out.
  const groupedProducts = useMemo(() => {
    const enrolled = new Set(currentProducts);
    const groups = {};
    Object.entries(catalog || {}).forEach(([key, def]) => {
      if (def?.hidden) return;
      if (enrolled.has(key)) return;
      const cat = def?.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({ key, ...def });
    });
    Object.values(groups).forEach(arr => arr.sort((a, b) => (a.label || "").localeCompare(b.label || "")));
    // Order categories per canonical sequence, then anything else alphabetically.
    const ordered = [];
    CATEGORY_ORDER.forEach(c => { if (groups[c]) ordered.push([c, groups[c]]); });
    Object.keys(groups).filter(c => !CATEGORY_ORDER.includes(c)).sort().forEach(c => ordered.push([c, groups[c]]));
    return ordered;
  }, [catalog, currentProducts]);

  const totalAvailable = useMemo(
    () => groupedProducts.reduce((sum, [, arr]) => sum + arr.length, 0),
    [groupedProducts]
  );

  // Iter 171.1 — collapsible category sections (matches the QuoteBuilder
  // catalog UX). Component returns null when closed (see below), so the
  // modal remounts on every open and we don't need a reset effect —
  // the lazy initializer below seeds the first non-empty category
  // expanded by default and the user toggles from there.
  const [expanded, setExpanded] = useState(() => {
    const init = new Set();
    return init; // populated by the post-mount default-seed effect-less pattern below
  });
  // Compute default expansion from the first non-empty category. This
  // memo refreshes if the catalog payload changes; since it's only
  // read inside `toggleCategory` / `isOpen` checks, no setState is
  // needed during render.
  const defaultExpanded = useMemo(() => {
    const first = groupedProducts.find(([, arr]) => arr.length > 0);
    return first ? new Set([first[0]]) : new Set();
  }, [groupedProducts]);
  // Mark which categories the user has explicitly toggled so we can
  // fall back to `defaultExpanded` for the rest. `userTouched` tracks
  // category ids the user has interacted with.
  const [userTouched, setUserTouched] = useState(() => new Set());
  const isCatOpen = (cat) =>
    userTouched.has(cat) ? expanded.has(cat) : defaultExpanded.has(cat);

  const toggleCategory = (cat) => {
    setExpanded(prev => {
      const next = new Set(prev);
      const currentlyOpen = isCatOpen(cat);
      if (currentlyOpen) next.delete(cat);
      else next.add(cat);
      return next;
    });
    setUserTouched(prev => {
      if (prev.has(cat)) return prev;
      const next = new Set(prev);
      next.add(cat);
      return next;
    });
  };

  const submit = async () => {
    if (!product) { toast.error("Pick a product first"); return; }
    setSaving(true);
    try {
      await axios.post(`${apiBase}/contacts/${contactId}/enrollments`, {
        product,
        status,
        patient_count: Number(patientCount) || 0,
        monthly_value: Number(monthlyValue) || 0,
        notes: notes || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const productLabel = catalog?.[product]?.label || product;
      toast.success(`${productLabel} added — ${status === "pending" ? "customer moved to Implementation" : "logged as active"}`);
      onCreated?.();
      reset();
      onClose?.();
    } catch (e) {
      toast.error(_pickErrorMessage(e, "Failed to add product"));
    }
    setSaving(false);
  };

  const reset = () => {
    setProduct(""); setStatus("pending"); setPatientCount(""); setMonthlyValue(""); setNotes(""); setPriceMode("preset");
  };

  if (!open) return null;

  return (
    <div
      data-testid="add-enrollment-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={() => { reset(); onClose?.(); }}>
      <div
        style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "auto", boxShadow: "0 22px 60px -12px rgba(15,23,42,0.45)" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "16px 22px", borderBottom: "0.5px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Package style={{ width: 16, height: 16, color: "var(--blue)" }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Add product enrollment</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Designate this customer for a new product. Pending status routes them to Implementation.</div>
            </div>
          </div>
          <button onClick={() => { reset(); onClose?.(); }} data-testid="add-enrollment-close" style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "#64748b" }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Product picker — collapsible spec-category sections (Iter 171.1) */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>Product</div>
            {totalAvailable === 0 ? (
              <div style={{ padding: 10, fontSize: 12, color: "var(--text-3)", background: "var(--paper)", border: "0.5px solid var(--border-soft)", borderRadius: 8, fontStyle: "italic" }}>
                Customer is already enrolled in every product in the catalog.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {groupedProducts.map(([cat, items]) => {
                  const isOpen = isCatOpen(cat);
                  const slug = cat.replace(/\s+/g, "-").toLowerCase();
                  return (
                    <div key={cat} data-testid={`enroll-category-${slug}`} style={{ border: "0.5px solid var(--border-soft)", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        data-testid={`enroll-category-toggle-${slug}`}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          width: "100%", padding: "9px 12px", background: isOpen ? "var(--paper)" : "#fff",
                          border: "none", borderBottom: isOpen ? "0.5px solid var(--border-soft)" : "none",
                          cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                          transition: "background-color 120ms ease",
                        }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {isOpen ? <ChevronDown style={{ width: 12, height: 12, color: "var(--text-2)" }} /> : <ChevronRight style={{ width: 12, height: 12, color: "var(--text-2)" }} />}
                          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{cat}</span>
                        </div>
                        <span style={{ fontSize: 10, color: "var(--text-3)", fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: "var(--paper)" }}>
                          {items.length}
                        </span>
                      </button>
                      {isOpen && (
                        <div style={{ padding: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                          {items.map(p => (
                            <button
                              key={p.key}
                              type="button"
                              data-testid={`enroll-product-${p.key}`}
                              onClick={() => handleProductSelect(p.key)}
                              style={{
                                padding: "10px 12px", borderRadius: 8, textAlign: "left",
                                background: product === p.key ? "var(--blue-soft)" : "#fff",
                                border: product === p.key ? "1px solid var(--blue)" : "0.5px solid var(--border)",
                                cursor: "pointer", fontFamily: "inherit",
                                transition: "background-color 120ms ease",
                              }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: product === p.key ? "var(--blue)" : "var(--text)" }}>{p.label}</div>
                              {p.per_patient ? (
                                <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 2 }}>${p.per_patient}/pt/mo (list)</div>
                              ) : null}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Status picker */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>Status</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {STATUS_OPTIONS.map(s => (
                <button
                  key={s.id}
                  data-testid={`enroll-status-${s.id}`}
                  onClick={() => setStatus(s.id)}
                  style={{
                    padding: "10px 14px", borderRadius: 8, textAlign: "left",
                    background: status === s.id ? (s.id === "pending" ? "#FEF3C7" : "var(--success-soft)") : "#fff",
                    border: status === s.id ? `1px solid ${s.id === "pending" ? "#F59E0B" : "var(--success)"}` : "0.5px solid var(--border)",
                    cursor: "pointer", fontFamily: "inherit",
                  }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: status === s.id ? (s.id === "pending" ? "#92400E" : "#065F46") : "var(--text)" }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Iter 171.2 — Pre-selectable prices / tiers (matches QuoteBuilder UX) */}
          {selectedDef ? (
            <div data-testid="enroll-price-section" style={{ background: "var(--paper)", borderRadius: 10, padding: 14, border: "0.5px solid var(--border-soft)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Pricing for {selectedDef.label}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                    List: <strong>{selectedDef.unit === "custom" || (selectedDef.list_price === 0 && selectedDef.unit !== "/mo") ? "Contact for pricing" : `$${selectedDef.list_price ?? selectedDef.per_patient ?? 0}${selectedDef.unit || ""}`}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="enroll-price-mode-toggle"
                  onClick={() => setPriceMode(priceMode === "custom" ? "preset" : "custom")}
                  style={{ fontSize: 10, fontWeight: 600, padding: "4px 10px", borderRadius: 12, border: "0.5px solid var(--border)", background: "#fff", color: "var(--text-2)", cursor: "pointer" }}>
                  {priceMode === "custom" ? "Use presets" : "Custom"}
                </button>
              </div>

              {/* Per-patient product: pick patient-count tier, auto-compute $ */}
              {isPerPatient ? (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>Patient count</div>
                  {priceMode === "preset" ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {PATIENT_TIERS.map(c => (
                        <button
                          key={c}
                          type="button"
                          data-testid={`enroll-patient-tier-${c}`}
                          onClick={() => handlePatientCountChange(String(c))}
                          style={{
                            padding: "8px 14px", borderRadius: 18, fontSize: 12, fontWeight: 600,
                            background: String(c) === patientCount ? "var(--blue)" : "#fff",
                            color: String(c) === patientCount ? "#fff" : "var(--text)",
                            border: String(c) === patientCount ? "1px solid var(--blue)" : "0.5px solid var(--border)",
                            cursor: "pointer", fontFamily: "inherit",
                          }}>
                          {c.toLocaleString()} pts
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      data-testid="enroll-patient-count"
                      type="number" min="0" step="1"
                      value={patientCount} onChange={(e) => handlePatientCountChange(e.target.value)}
                      placeholder="e.g. 150"
                      className="input num" style={{ fontSize: 13 }} />
                  )}
                  <div style={{ marginTop: 12, padding: "10px 12px", background: "#fff", borderRadius: 8, border: "0.5px solid var(--border-soft)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: "var(--text-2)" }}>Estimated monthly value</span>
                    <span data-testid="enroll-monthly-value-readout" style={{ fontSize: 16, fontWeight: 700, color: "var(--blue)" }}>
                      ${Number(monthlyValue || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              ) : isPercentage ? (
                /* Percentage product: pick a % of collections tier */
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>% of monthly collections</div>
                  {priceMode === "preset" ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {PERCENT_TIERS.map(p => (
                        <button
                          key={p}
                          type="button"
                          data-testid={`enroll-percent-tier-${p}`}
                          onClick={() => setMonthlyValue(String(p))}
                          style={{
                            padding: "8px 14px", borderRadius: 18, fontSize: 12, fontWeight: 600,
                            background: String(p) === monthlyValue ? "var(--blue)" : "#fff",
                            color: String(p) === monthlyValue ? "#fff" : "var(--text)",
                            border: String(p) === monthlyValue ? "1px solid var(--blue)" : "0.5px solid var(--border)",
                            cursor: "pointer", fontFamily: "inherit",
                          }}>
                          {p}%
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      data-testid="enroll-monthly-value"
                      type="number" min="0" step="0.5"
                      value={monthlyValue} onChange={(e) => setMonthlyValue(e.target.value)}
                      placeholder="e.g. 4.5"
                      className="input num" style={{ fontSize: 13 }} />
                  )}
                  <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-3)", fontStyle: "italic" }}>
                    Final $ depends on the practice&apos;s monthly collections — captured downstream in the quote.
                  </div>
                </div>
              ) : (
                /* Fixed-fee product: pick a monthly $ tier */
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>Monthly value</div>
                  {priceMode === "preset" ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {FIXED_TIERS.map(t => (
                        <button
                          key={t}
                          type="button"
                          data-testid={`enroll-price-tier-${t}`}
                          onClick={() => setMonthlyValue(String(t))}
                          style={{
                            padding: "8px 14px", borderRadius: 18, fontSize: 12, fontWeight: 600,
                            background: String(t) === monthlyValue ? "var(--blue)" : "#fff",
                            color: String(t) === monthlyValue ? "#fff" : "var(--text)",
                            border: String(t) === monthlyValue ? "1px solid var(--blue)" : "0.5px solid var(--border)",
                            cursor: "pointer", fontFamily: "inherit",
                          }}>
                          ${t.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      data-testid="enroll-monthly-value"
                      type="number" min="0" step="50"
                      value={monthlyValue} onChange={(e) => setMonthlyValue(e.target.value)}
                      placeholder="e.g. 1200"
                      className="input num" style={{ fontSize: 13 }} />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: 10, fontSize: 11, color: "var(--text-3)", background: "var(--paper)", border: "0.5px dashed var(--border-soft)", borderRadius: 8, fontStyle: "italic", textAlign: "center" }}>
              Pick a product above to see pricing options.
            </div>
          )}

          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>Notes <span style={{ color: "var(--text-4)", fontWeight: 400 }}>(opt.)</span></div>
            <textarea
              data-testid="enroll-notes"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Upsold during QBR · expected go-live Aug 15"
              className="input" rows={2}
              style={{ fontSize: 12, resize: "vertical" }} />
          </div>
        </div>

        <div style={{ padding: "14px 22px", borderTop: "0.5px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button data-testid="add-enrollment-cancel" onClick={() => { reset(); onClose?.(); }} className="btn" style={{ background: "#fff", color: "#475569", border: "0.5px solid #e2e8f0" }}>Cancel</button>
          <button
            data-testid="add-enrollment-submit"
            onClick={submit}
            disabled={!product || saving || totalAvailable === 0}
            className="btn btn-primary"
            style={{ opacity: (!product || totalAvailable === 0) ? 0.5 : 1 }}>
            {saving ? <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> : <Plus style={{ width: 13, height: 13 }} />}
            Add product
          </button>
        </div>
      </div>
    </div>
  );
}

function _pickErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join("; ");
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
}
